import ArrowBackLight from "@assets/navigation/back.svg";
import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import LightCustomButton from "@components/buttons/LightCustomButton";
import { invoiceServices } from "@services/apis/Invoice";
import { GetOfframpChargesData } from "@services/models/InvoiceApprovals/GetOfframpChargesModel";
import { formatDisplayDate } from "@utils/safeDate";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import EditOfframpChargeSlider from "./components/EditOfframpChargeSlider";
import OfframpChargeManagementSlider from "./components/OfframpChargeManagementSlider";
import StatusPillOfframpChargeManagement from "./components/StatusPillOfframpChargeManagement";

const OfframpChargeManagement = () => {
  const gridRef = useRef<AgGridReact>(null);

  const navigate = useNavigate();

  const [chargeData, setChargeData] = useState<GetOfframpChargesData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const [addChargeSliderOpen, setAddChargeSliderOpen] =
    useState<boolean>(false);
  const [editChargeSliderOpen, setEditChargeSliderOpen] =
    useState<boolean>(false);
  const [selectedCharge, setSelectedCharge] =
    useState<GetOfframpChargesData | null>(null);

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Created At",
        field: "createdAt",
        flex: 1.2,
        minWidth: 120,
        maxWidth: 220,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agDateColumnFilter",
        cellRenderer: (params: any) => {
          return (
            <div className="flex items-center h-full">
              <span className="text-[10px] text-text-base">
                {formatDisplayDate(params.value)}
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Charge ID",
        field: "chargeId",
        flex: 0.8,
        minWidth: 100,
        maxWidth: 110,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Merchant ID",
        field: "merchantId",
        flex: 1.0,
        minWidth: 120,
        maxWidth: 140,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        cellRenderer: (params: any) => {
          return (
            <div className="flex items-center h-full">
              <span className="text-[10px] text-text-base font-medium">
                {params.value || "Default"}
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Currency",
        field: "currencySymbol",
        flex: 1.0,
        minWidth: 120,
        maxWidth: 140,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        cellRenderer: (params: any) => {
          return (
            <div className="flex items-center h-full">
              <span className="text-[10px] text-text-base font-medium">
                {params.value || "Default"}
              </span>
            </div>
          );
        },
      },

      {
        headerName: "Charge %",
        field: "chargePercentage",
        flex: 1.0,
        minWidth: 100,
        maxWidth: 150,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        cellRenderer: (params: any) => {
          return (
            <div className="flex items-center h-full">
              <span className="text-[10px] text-text-base font-medium">
                {params.value}%
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Description",
        field: "description",
        flex: 1.5,
        minWidth: 150,
        maxWidth: 300,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => {
          return (
            <div className="flex items-center h-full">
              <span className="text-[10px] text-text-base">
                {params.value || "No description"}
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Created By",
        field: "createdBy",
        flex: 0.9,
        minWidth: 90,
        maxWidth: 120,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Status",
        field: "isActive",
        flex: 0.9,
        minWidth: 90,
        maxWidth: 120,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => {
          return (
            <StatusPillOfframpChargeManagement
              statusText={params.data.isActive ? "ACTIVE" : "DEACTIVATED"}
            />
          );
        },
      },
      {
        headerName: "Edit",
        field: "id",
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        flex: 0.6,
        minWidth: 60,
        maxWidth: 190,
        filter: null,
        cellRenderer: (params: any) => {
          return (
            <div className="flex justify-start">
              <div
                className="flex justify-center items-center cursor-pointer shadow-[0px_2px_4px_0px_rgba(0,0,0,0.08)] bg-border-button-secondary border border-bg-button-secondary"
                style={{ width: "20px", height: "20px", borderRadius: "4px" }}
                onClick={() => {
                  setSelectedCharge(params.data);
                  setEditChargeSliderOpen(true);
                }}
              >
                <span className="text-[14px] material-symbols-outlined text-text-base">
                  border_color
                </span>
              </div>
            </div>
          );
        },
      },
    ],
    []
  );

  const fetchChargeData = async () => {
    try {
      setLoading(true);
      const response = await invoiceServices.getOfframpCharges();
      setChargeData(response.data);
    } catch (err: any) {
      setError("Failed to fetch offramp charge data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChargeData();
  }, []);

  return (
    <>
      <div className="h-[70px] border-b border-border-subtle bg-bg-page-body flex flex-row sticky top-0 bg-white z-10 pl-[20px] w-full">
        <div className="flex flex-grow justify-between items-center w-full mr-4">
          <div className="flex gap-2 items-center">
            <img
              src={ArrowBackLight}
              alt="Back"
              className="cursor-pointer"
              onClick={() => navigate("/invoice-approvals")}
            />
            <p className="flex text-text-base text-[14px] font-semibold items-center">
              Offramp Charge Management
            </p>
          </div>
          <LightCustomButton
            text="+ ADD CHARGE"
            isActive={true}
            onClick={() => setAddChargeSliderOpen(true)}
          />
        </div>
      </div>

      <div className="flex flex-col bg-bg-page-body py-[20px] px-[12px] h-full">
        {loading ? (
          <AgGridSkeleton
            columnDefs={columnDefs}
            style={{ height: "calc(100vh - 110px)", width: "100%" }}
          />
        ) : (
          <AgGridReactTable
            ref={gridRef}
            rowData={chargeData}
            columnDefs={columnDefs}
            getRowId={(params) => params.data.chargeId?.toString()}
            error={error}
            style={{ height: "calc(100vh - 110px)", width: "100%" }}
          />
        )}
      </div>
      <OfframpChargeManagementSlider
        isOpen={addChargeSliderOpen}
        onClose={() => setAddChargeSliderOpen(false)}
        onChargeAdded={() => fetchChargeData()}
      />
      <EditOfframpChargeSlider
        isOpen={editChargeSliderOpen}
        onClose={() => {
          setEditChargeSliderOpen(false);
          setSelectedCharge(null);
        }}
        onChargeUpdated={() => fetchChargeData()}
        chargeData={selectedCharge}
      />
    </>
  );
};

export default OfframpChargeManagement;
